/* ──────────────────────────────────────────────────────────────────────
 *  ContentPage — Unified content list with bookshelf header
 *
 *  Replaces both ChroniclesPage and KnowledgePage.
 *  Fetches existing chronicles via API and displays them as content cards.
 *  Bookshelf filters by template/category type.
 *
 *  Route: /content
 * ────────────────────────────────────────────────────────────────────── */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import ContentBookshelf from '../../Components/ContentBookshelf/ContentBookshelf';
import type { BookshelfTemplateItem } from '../../Components/ContentBookshelf/ContentBookshelf';
import TemplateSelector from '../../Components/TemplateSelector/TemplateSelector';
import CreateChronicleModal from '../../Components/CreateChronicleModal/CreateChronicleModal';
import AiSeedModal from '../../Components/AiSeedModal/AiSeedModal';
import { getIsSystemAdmin } from '../../auth/AuthContext';
import { fetchChronicles } from '../../api/chronicles';
import type { ChronicleSummary } from '../../api/chronicles';
import { mediaSrc } from '../../api/mediaUrl';
import {
  CONTENT_TEMPLATES,
  CONTENT_TEMPLATES_ORDERED,
  getTemplatesForTier,
  getLockedTemplates,
  LEGACY_KEY_MAP,
  type ContentTemplateKey,
  type TrustTier,
} from '../../Data/Content/contentTemplates';
import styles from './ContentPage.module.css';

/* ── Helpers ────────────────────────────────────────────────────── */

function resolveImageUrl(raw: string | undefined | null): string | null {
  return mediaSrc(raw);
}

function shortDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

/** Map a chronicle's categoryKey to a ContentTemplateKey (or null if blog). */
function resolveTemplateKey(categoryKey: string): ContentTemplateKey | null {
  // Direct match
  if (categoryKey in CONTENT_TEMPLATES) return categoryKey as ContentTemplateKey;
  // Legacy mapping. DEV-1381: `opinion`/`personal` now resolve to journal_entry
  // (they used to map to the non-template 'blog' sentinel, which is gone).
  const mapped = LEGACY_KEY_MAP[categoryKey];
  if (mapped && mapped in CONTENT_TEMPLATES) return mapped as ContentTemplateKey;
  return null;
}

/* ── Dimension colour map ────────────────────────────────────── */

const DIMENSION_COLORS: Record<string, string> = {
  mind:   'var(--dim-mind, #7ea3cc)',
  body:   'var(--dim-body, #6aab7a)',
  heart:  'var(--dim-heart, #c77a6f)',
  wings:  'var(--dim-wings, #c9a24e)',
  roots:  'var(--dim-roots, #8B7355)',
  voice:  'var(--dim-voice, #d17eb2)',
};

/* ── Props ────────────────────────────────────────────────────── */

interface ContentPageProps {
  currentUserGuid: string;
}

/* ── Component ────────────────────────────────────────────────── */

export default function ContentPage({ currentUserGuid }: ContentPageProps) {
  const { t } = useAppTranslation('pages');
  const { t: tc } = useAppTranslation('content');
  const navigate = useNavigate();
  const mountedRef = useRef(true);

  /* ---- Placeholder: user trust tier (will come from merit system) ---- */
  const userTrustTier: TrustTier = 'contributor';

  /* ---- Data fetching ---- */
  const [chronicles, setChronicles] = useState<ChronicleSummary[]>([]);
  const [loading, setLoading] = useState(true);

  const loadContent = useCallback(async () => {
    if (!currentUserGuid) return;
    setLoading(true);
    try {
      const params: Record<string, string> = {
        InitiatorGuid: currentUserGuid,
        PageSize: '100',
      };
      const result = await fetchChronicles(params);
      if (mountedRef.current) {
        setChronicles(result.items ?? []);
      }
    } catch {
      if (mountedRef.current) {
        setChronicles([]);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [currentUserGuid]);

  useEffect(() => {
    mountedRef.current = true;
    loadContent();
    return () => { mountedRef.current = false; };
  }, [loadContent]);

  /* ---- Bookshelf data: count content per template ---- */
  const templateCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const c of chronicles) {
      const key = resolveTemplateKey(c.categoryKey);
      if (key) {
        counts[key] = (counts[key] ?? 0) + 1;
      }
    }
    return counts;
  }, [chronicles]);

  // Mini-Project is its own entity (created/managed on the projects side), not a
  // content shelf — hide it from the bookshelf (Skip 2026-06-17).
  const unlockedTemplates: BookshelfTemplateItem[] = useMemo(
    () =>
      getTemplatesForTier(userTrustTier)
        .filter((tmpl) => tmpl.key !== 'mini_project')
        .map((tmpl) => ({
          template: tmpl,
          count: templateCounts[tmpl.key] ?? 0,
        })),
    [userTrustTier, templateCounts],
  );

  const lockedTemplates = useMemo(
    () => getLockedTemplates(userTrustTier).filter((tmpl) => tmpl.key !== 'mini_project'),
    [userTrustTier],
  );

  /* ---- Filter state ---- */
  const [selectedTemplate, setSelectedTemplate] = useState<ContentTemplateKey | null>(null);

  /* ---- Template selector modal state ---- */
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showAiSeed, setShowAiSeed] = useState(false);

  const handleSelect = useCallback((key: ContentTemplateKey | null) => {
    setSelectedTemplate(key);
  }, []);

  const handleNewContent = useCallback(() => {
    // DEV-1336 #2: create a draft via the lightweight modal and land on the
    // detail page — no longer the legacy full-page editor.
    setShowCreate(true);
  }, []);

  const handleCloseSelector = useCallback(() => {
    setShowTemplateSelector(false);
  }, []);

  const handleTemplateChosen = useCallback(
    (key: ContentTemplateKey) => {
      setShowTemplateSelector(false);
      navigate(`/chronicles/new?template=${key}`);
    },
    [navigate],
  );

  const unlockedTemplateDefs = useMemo(
    () => getTemplatesForTier(userTrustTier),
    [userTrustTier],
  );

  /* ---- Filtered chronicles ---- */
  const filteredChronicles = useMemo(() => {
    if (!selectedTemplate) return chronicles;
    return chronicles.filter((c) => {
      const key = resolveTemplateKey(c.categoryKey);
      return key === selectedTemplate;
    });
  }, [chronicles, selectedTemplate]);

  /* ---- Navigation handlers ---- */
  const handleCardClick = useCallback(
    (guid: string) => {
      navigate(`/chronicles/${guid}/view`);
    },
    [navigate],
  );

  const handleEdit = useCallback(
    (guid: string) => {
      // Editing now happens on the detail page (inline + settings modal).
      navigate(`/chronicles/${guid}/view`);
    },
    [navigate],
  );

  return (
    <div className={styles.container}>
      {/* ── Bookshelf header ── */}
      <ContentBookshelf
        templates={unlockedTemplates}
        lockedTemplates={lockedTemplates}
        selectedTemplate={selectedTemplate}
        onSelect={handleSelect}
        onNewContent={handleNewContent}
      />

      {/* ── Admin: AI seeding (DEV-1354) ── */}
      {getIsSystemAdmin() && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 24px 0' }}>
          <button
            type="button"
            onClick={() => setShowAiSeed(true)}
            style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid var(--accent-border)', background: 'var(--accent-bg)', color: 'var(--accent-text)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            ✦ Seed with AI
          </button>
        </div>
      )}

      {/* ── Content list ── */}
      <div className={`${styles.contentArea} pageContainer`}>
        {loading ? (
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
          </div>
        ) : filteredChronicles.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>📚</div>
            <h2 className={styles.emptyTitle}>
              {t('content.emptyTitle', 'Content')}
            </h2>
            <p className={styles.emptyDescription}>
              {selectedTemplate
                ? t('content.filterActive', 'Filtering by: {{template}}', {
                    template: tc(`templates.${selectedTemplate}.name`),
                  })
                : t('content.emptyDescription', 'Your content library will appear here. Use the bookshelf above to filter by template, or tap + to create something new.')}
            </p>
          </div>
        ) : (
          <div className={styles.cardGrid}>
            {filteredChronicles.map((chronicle) => {
              const heroUrl = resolveImageUrl(chronicle.heroImageUrl);
              const templateKey = resolveTemplateKey(chronicle.categoryKey);
              const template = templateKey ? CONTENT_TEMPLATES[templateKey] : null;

              return (
                <div
                  key={chronicle.chronicleGuid}
                  className={styles.card}
                  onClick={() => handleCardClick(chronicle.chronicleGuid)}
                  role="link"
                  tabIndex={0}
                  onKeyDown={(e) =>
                    e.key === 'Enter' && handleCardClick(chronicle.chronicleGuid)
                  }
                  style={{
                    '--card-accent': template?.bookshelf.spineColorToken ?? 'var(--accent-primary)',
                  } as React.CSSProperties}
                >
                  {/* Colored top accent bar */}
                  <div className={styles.cardAccent} />

                  {/* Hero image or fallback */}
                  {heroUrl ? (
                    <div
                      className={styles.cardHero}
                      style={{ backgroundImage: `url(${heroUrl})` }}
                    />
                  ) : (
                    <div className={styles.cardHeroFallback}>
                      <span className={styles.cardHeroIcon}>
                        {template?.bookshelf.icon ?? '📄'}
                      </span>
                    </div>
                  )}

                  {/* Card body */}
                  <div className={styles.cardBody}>
                    {/* Badges row */}
                    <div className={styles.cardBadges}>
                      {chronicle.status === 'draft' && (
                        <span className={`${styles.statusBadge} ${styles.statusDraft}`}>
                          Draft
                        </span>
                      )}
                      {chronicle.status === 'published' && (
                        <span className={`${styles.statusBadge} ${styles.statusPublished}`}>
                          Published
                        </span>
                      )}
                      {template && (
                        <span className={styles.templateBadge}>
                          {template.bookshelf.icon} {tc(`templates.${template.key}.name`)}
                        </span>
                      )}
                    </div>

                    <h3 className={styles.cardTitle}>{chronicle.title}</h3>

                    {chronicle.subtitle && (
                      <p className={styles.cardSubtitle}>{chronicle.subtitle}</p>
                    )}

                    <div className={styles.cardMeta}>
                      <span>{shortDate(chronicle.updatedAt)}</span>
                    </div>


                    {/* Actions */}
                    <div
                      className={styles.cardActions}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        className={styles.editBtn}
                        onClick={() => handleEdit(chronicle.chronicleGuid)}
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Template selector modal ── */}
      {showTemplateSelector && (
        <TemplateSelector
          templates={unlockedTemplateDefs}
          onSelect={handleTemplateChosen}
          onClose={handleCloseSelector}
        />
      )}

      {/* ── New chronicle: draft → detail page (DEV-1336 #2) ── */}
      {showCreate && (
        <CreateChronicleModal
          templates={unlockedTemplateDefs}
          onClose={() => setShowCreate(false)}
          onCreated={(guid) => { setShowCreate(false); navigate(`/chronicles/${guid}/view`); }}
        />
      )}

      {showAiSeed && (
        <AiSeedModal
          onClose={() => setShowAiSeed(false)}
          onOpenDraft={(guid) => { setShowAiSeed(false); navigate(`/chronicles/${guid}/view`); }}
        />
      )}
    </div>
  );
}
